/**
 * Super-Admin inline content editing — platform wide.
 *
 * Usage:
 *   <Editable keyId="home.hero.headline" as="h1" className="text-4xl font-bold">
 *     The Exchange Alliance
 *   </Editable>
 *
 * - Any UI text wrapped in <Editable> is addressable by keyId.
 * - On mount the ContentProvider fetches /api/content/labels and caches
 *   overrides. If a key has an override it renders that; otherwise the
 *   fallback (children) renders.
 * - When the logged-in user has is_super_admin === true, hovering any
 *   editable text reveals a pencil button. Clicking opens a popover with
 *   a textarea + Save/Cancel. Save PATCHes /api/content/labels/{key},
 *   updates the context, and the new text appears instantly for everyone.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Ctx = createContext({ labels: {}, setLabel: () => {}, loading: false });

export function ContentProvider({ children }) {
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/content/labels`).then(({ data }) => setLabels(data.labels || {})).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const setLabel = useCallback((key, text) => {
    setLabels((prev) => ({ ...prev, [key]: text }));
  }, []);

  return <Ctx.Provider value={{ labels, setLabel, loading }}>{children}</Ctx.Provider>;
}

export const useContent = () => useContext(Ctx);

const token = () => { try { return localStorage.getItem("nex_token"); } catch { return null; } };

export function Editable({ keyId, as = "span", children, className = "", style, placeholder = "" }) {
  const { labels, setLabel } = useContent();
  const { user } = useAuth();
  const canEdit = !!user?.is_super_admin;

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [coords, setCoords] = useState({ left: 0, top: 0 });
  const inputRef = useRef(null);

  const current = labels[keyId] ?? (typeof children === "string" ? children : "");
  const Tag = as;

  const start = () => {
    setDraft(current || "");
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 20);
  };

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === current) { setOpen(false); return; }
    setSaving(true);
    try {
      await axios.patch(
        `${API}/content/labels/${encodeURIComponent(keyId)}`,
        { text: trimmed },
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      setLabel(keyId, trimmed);
      setOpen(false);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Save failed — check that you are signed in as Super Admin.");
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return <Tag className={className} style={style} data-content-key={keyId}>{current || placeholder}</Tag>;
  }

  const openPopover = () => {
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      setCoords({ left: r.left + window.scrollX, top: r.bottom + window.scrollY + 6 });
    }
    start();
  };

  return (
    <>
      <Tag ref={setAnchor} className={`relative group ${className}`} style={style} data-content-key={keyId}>
        {current || <span className="opacity-50 italic">{placeholder || `[${keyId}]`}</span>}
        <button
          type="button"
          onClick={openPopover}
          aria-label={`Edit ${keyId}`}
          data-testid={`editable-pencil-${keyId}`}
          className="ml-1 align-middle opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center h-5 w-5 rounded-sm bg-amber-300/90 hover:bg-amber-200 text-slate-900 shadow-sm"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </Tag>
      {open && ReactDOM.createPortal(
        <div
          data-testid={`editable-popover-${keyId}`}
          className="fixed z-[9999] w-[320px] rounded-md bg-slate-900 text-slate-100 border border-amber-400/60 shadow-2xl p-3"
          style={{ left: coords.left, top: coords.top, fontFamily: "Inter, sans-serif", fontSize: "13px", lineHeight: 1.4, color: "#f1f5f9" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-[10px] font-mono tracking-widest uppercase text-amber-300 mb-1">Edit · {keyId}</div>
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save(); if (e.key === "Escape") setOpen(false); }}
            rows={Math.min(8, Math.max(2, draft.split("\n").length + 1))}
            className="w-full bg-slate-800 text-slate-100 p-2 text-sm rounded-sm border border-slate-700 focus:outline-none focus:border-amber-400"
            data-testid={`editable-input-${keyId}`}
          />
          <div className="flex items-center justify-between gap-2 mt-2">
            <div className="text-[10px] text-slate-400">⌘/Ctrl + Enter saves</div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setOpen(false)} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-300 hover:text-white" data-testid={`editable-cancel-${keyId}`}>
                <X className="h-3 w-3" /> Cancel
              </button>
              <button type="button" onClick={save} disabled={saving}
                className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-sm bg-amber-300 text-slate-900 font-semibold disabled:opacity-50"
                data-testid={`editable-save-${keyId}`}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
